// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Direction = 'asc' | 'desc';

interface AppsSortMenuProps {
  sort: { key: string; direction: Direction } | null;
  isIT: boolean;
  onSortChange: (next: { key: string; direction: Direction } | null) => void;
}

const SORT_KEYS: Array<{ key: string; label: string; itOnly?: boolean }> = [
  { key: 'slug', label: 'Name' },
  { key: 'status', label: 'Status' },
  { key: 'usage', label: 'Usage', itOnly: true },
  { key: 'lastUsed', label: 'Last used', itOnly: true },
];

export function AppsSortMenu({ sort, isIT, onSortChange }: AppsSortMenuProps): React.JSX.Element {
  const keys = SORT_KEYS.filter((k) => isIT || !k.itOnly);
  const active = keys.find((k) => k.key === sort?.key);
  const DirectionIcon = sort?.direction === 'desc' ? ArrowDownNarrowWide : ArrowUpNarrowWide;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="apps-sort">
          {active ? (
            <DirectionIcon className="size-4" aria-hidden />
          ) : (
            <ArrowUpDown className="size-4" aria-hidden />
          )}
          {active ? active.label : 'Sort'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Sort by</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sort?.key ?? ''}
          onValueChange={(key) => onSortChange({ key, direction: sort?.direction ?? 'asc' })}
        >
          {keys.map((k) => (
            <DropdownMenuRadioItem key={k.key} value={k.key}>
              {k.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={sort?.direction ?? 'asc'}
          onValueChange={(direction) =>
            onSortChange({ key: sort?.key ?? 'slug', direction: direction as Direction })
          }
        >
          <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        {sort && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onSortChange(null)}>Reset</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
